// =====================================================
// BOOKMARKS in the redline: one bookmark of each name, each id used once.
//
// A bookmark is what a cross-reference points at (a heading's "_Ref…", a table of contents'
// "_Toc…"). Both drafts usually have the same ones, numbered each in its own way. A bookmark copied
// from the original (a deleted paragraph's) is tagged FRB-N as it is written (tagCrossRelIds):
//
// - When the revised draft's bookmark of that name is in the redline too, the original's is left
//   out. Word keeps only the first bookmark of a name, which was often the deleted copy: after Accept
//   All the revised draft's cross-references then pointed at nothing.
// - Otherwise it keeps its id, or gets one of its own when the revised draft's bookmarks use it.
//
// A draft's own second bookmark of a name is left out too (Word ignores it). Then a bookmark whose
// start or end is missing (its other end was in a paragraph written from the other draft) is closed
// where it starts, or its end dropped (balanceBookmarks).
// =====================================================

import { balanceBookmarks } from './anchors';

const PARTS = /^word\/(document|footnotes|endnotes|comments)\.xml$/;
const TAG = /<w:bookmark(Start|End)\b[^>]*?\/>/g;

export async function resolveBookmarks(newZip: any): Promise<void> {
  const names = Object.keys(newZip.files).filter((n: string) => PARTS.test(n));
  const xmls = new Map<string, string>();
  for (const n of names) xmls.set(n, await newZip.file(n).async('string'));
  const revNames = new Set<string>(), revIds = new Set<string>();
  const origName = new Map<string, string>(), origIds: string[] = [];
  let max = 0;
  for (const x of xmls.values()) for (const m of x.matchAll(TAG)) {
    const id = m[0].match(/\bw:id="(FRB-)?(-?\d+)"/);
    if (!id) continue;
    max = Math.max(max, Number(id[2]));
    const name = m[1] === 'Start' ? (m[0].match(/\bw:name="([^"]*)"/) || [])[1] : undefined;
    if (!id[1]) { revIds.add(id[2]); if (name != null) revNames.add(name); continue; }
    if (!origIds.includes(id[2])) origIds.push(id[2]);
    if (name != null && !origName.has(id[2])) origName.set(id[2], name);
  }
  if (origIds.length) {
    const target = new Map<string, string | null>();
    for (const id of origIds) {
      const name = origName.get(id);
      if (name == null || revNames.has(name)) target.set(id, null);
      else target.set(id, revIds.has(id) ? String(++max) : id);
    }
    for (const [n, x] of xmls) {
      const y = x.replace(/<w:bookmark(?:Start|End)\b[^>]*?\bw:id="FRB-(-?\d+)"[^>]*?\/>/g, (m: string, id: string) => {
        const t = target.get(id);
        return t == null ? '' : m.replace(`"FRB-${id}"`, `"${t}"`);
      });
      if (y !== x) { xmls.set(n, y); newZip.file(n, y); }
    }
  }
  const body = xmls.get('word/document.xml');
  if (body != null) { const done = balanceBookmarks(laterOfName(body)); if (done !== body) newZip.file('word/document.xml', done); }
}

/** A draft that itself has two bookmarks of a name (Word keeps the first and ignores the rest when
 *  it opens the file): the redline leaves the later ones out, start and end. */
function laterOfName(xml: string): string {
  const seen = new Map<string, string>(), drop = new Set<string>();
  for (const m of xml.matchAll(/<w:bookmarkStart\b[^>]*\/>/g)) {
    const name = (m[0].match(/\bw:name="([^"]*)"/) || [])[1], id = (m[0].match(/\bw:id="([^"]*)"/) || [])[1];
    if (name == null || id == null) continue;
    if (!seen.has(name)) seen.set(name, id);
    else if (seen.get(name) !== id && ![...seen.values()].includes(id)) drop.add(id);
  }
  if (!drop.size) return xml;
  return xml.replace(/<w:bookmark(?:Start|End)\b[^>]*\/>/g, (m) => (drop.has((m.match(/\bw:id="([^"]*)"/) || [])[1]) ? '' : m));
}
